
export interface AppNotification {
  id: string;
  userId: string; // The person who receives the notification (Teacher/Student)
  title: string;
  message: string;
  type: 'info' | 'urgent' | 'success' | 'warning';
  timestamp: string;
  relatedId?: string; // e.g. tugasId, studentId
  isRead: boolean;
  senderName?: string;
  senderAvatar?: string;
}

export enum UserRole {
  Admin = 'Super Admin',
  Guru = 'Guru',
  Siswa = 'Siswa',
}

export enum View {
  Dashboard = 'Dashboard',
  // Admin Views
  ManageTeachers = 'Kelola Guru',
  ManageClasses = 'Kelola Kelas',
  ManageStudents = 'Kelola Siswa',
  AdminAgenda = 'Agenda Guru',
  Settings = 'Pengaturan Sekolah',
  PeriodSettings = 'Jam Pelajaran',
  Database = 'Database',
  // Guru Views
  Schedule = 'Jadwal Mengajar',
  LearningObjectives = 'Tujuan Pembelajaran',
  Agenda = 'Agenda Harian',
  Attendance = 'Absensi Siswa',
  Grading = 'Input Nilai',
  NilaiRapor = 'Nilai Rapor',
  MaterialGenerator = 'Pengelolaan Materi',
  Tugas = 'Tugas',
  StudentList = 'Daftar Siswa',
  Profile = 'Profil Saya',
  StudentProfile = 'Profil Siswa',
  StudentGrade = 'Nilai Saya',
  SubjectSelection = 'Pilih Mapel',
  ProfileAccessHistory = 'Riwayat Akses',
  Materi = 'Materi',
  StudentAgenda = 'Agenda Siswa',
  WaliRombel = 'Wali Rombel',
  Perwalian = 'Administrasi Walas',
}

export interface Teacher {
  id: string;
  name: string;
  username: string;
  password: string;
  subject: string;
  assignedClasses: string[];
  homeroomClass?: string;
  nip?: string;
  GradeVisibility?: boolean;
  RaporVisibility?: boolean;
  SasVisibility?: boolean;
  sumatifWeight?: number;
  praktikumWeight?: number;
  raporSumatifWeight?: number;
  raporPraktikumWeight?: number;
  raporSasWeight?: number;
  weightDaily?: number;
  weightSas?: number;
  dismissedNotifications?: string[];
}

export interface LearningObjective {
  id: string;
  code: string; // Misal: TP 1.1
  element?: string; // Elemen
  scope?: string; // Lingkup Materi
  semester?: string; // Semester
  academicYear?: string; // Tahun Ajaran
  description: string; // Isi Tujuan Pembelajaran
  subject: string;
  isSummative?: boolean;
  isPracticum?: boolean;
}

export interface LearningContent {
  id: string;
  tpid: string;
  LearningContent: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Student {
  id: string;
  name: string;
  nis: string;
  nisn?: string;
  class: string;
  gender: 'Laki-laki' | 'Perempuan';
  status: 'Aktif' | 'Tidak Aktif';
  classRole?: string; // 'KM' | 'Wakil KM' | 'Sekretaris' | 'Bendahara' | string
  notes?: string;
  classRoleNote?: string;
  subjects?: string[];
  dismissedNotifications?: string[];

  // Data Identitas
  nickname?: string;
  pob?: string; // Tempat lahir
  dob?: string; // Tanggal lahir
  nik?: string;
  elementaryCertNo?: string; // Nomor Ijazah SD/MI
  religion?: string;
  address?: string;
  postalCode?: string; // Kode Pos Siswa
  phone?: string;

  // Data Orang Tua / Wali
  fatherName?: string;
  motherName?: string;
  guardianName?: string;
  parentPhone?: string;
  parentJob?: string;
  parentAddress?: string;
  parentPostalCode?: string; // Kode Pos Orang Tua / Wali
  emergencyContact?: string;

  // Data Pendukung
  photoUrl?: string;
  familyCardNo?: string;
  supportingDocUrl?: string;
  specialNotes?: string;
  kipPipStatus?: string;
  rencanaStudiLanjutan?: string;
  rencanaStudiDetail?: string;
}

export interface LearningMaterial {
    title: string;
    subject: string;
    grade: string;
    explanation?: string;
    examples?: string[];
    summary?: string;
    tugas?: {
      question: string;
      options: string[];
      answer: string;
    }[];
    lkpd?: string;
    quizHots?: {
      question: string;
      answerKey: string;
    }[];
}

export interface LessonPlan {
  materiPokok: string;
  tujuanPembelajaran: string[] | string;
  kegiatanPendahuluan: string[] | string;
  kegiatanInti: string[] | string;
  kegiatanPenutup: string[] | string;
  penilaian: {
    sikap: string;
    pengetahuan: string;
    keterampilan: string;
  };
  sumberBelajar: string[] | string;
}

export interface ProfileAccessLog {
  id: string;
  studentId: string;
  studentClass?: string; // Class of the student whose profile is being accessed
  accessorId: string;
  accessorName: string;
  accessorNis: string; // NIS for student, NIP for teacher, or role name for admin
  accessorRole: UserRole;
  timestamp: string;
  device: string;
  os: string;
  ip: string;
}

export enum AttendanceStatus {
  Hadir = 'Hadir',
  Izin = 'Izin',
  Sakit = 'Sakit',
  Alpa = 'Alpa',
  Dispen = 'Dispen',
  Terlambat = 'Terlambat',
  Pulang = 'Pulang Sebelum Waktunya',
}

export interface AttendanceRecord {
  id: string;
  studentId: string;
  agendaId: string; // Menghubungkan absensi ke agenda spesifik
  date: string;
  status: AttendanceStatus;
  description?: string;
  class?: string | null;
  teacherId?: string | null;
}

// Update ActivityType to be an enum for better consistency
export enum ActivityType {
  KBM = 'KBM',
  PEMBELAJARAN_DAN_ASESMEN = 'KBM dan Asesmen Harian',
  KBM_DAN_QUIZ = 'KBM dan Quiz',
  SUMATIF = 'SUMATIF',
  SAS = 'SAS',
  ADMINISTRASI = 'Administrasi',
  EKSTRAKURIKULER = 'Ekstrakurikuler',
  PRAKTIKUM = 'Praktikum',
  TUGAS_MANDIRI = 'Tugas Mandiri',
  KEGIATAN_LOMBA = 'Kegiatan lomba',
  KEGIATAN_KESISWAAN = 'Kegiatan kesiswaan',
  KEGIATAN_SEKOLAH = 'Kegiatan sekolah',
  PERWALIAN = 'Perwalian',
}

export interface AgendaEntry {
  id: string;
  date: string;
  class: string;
  subject: string;
  topic: string;
  activityType?: ActivityType; // Use enum
  penilaian?: string; // New field
  startPeriod: number;
  endPeriod: number;
  isExtra?: boolean; // Indicates if this agenda was created ad-hoc
  tpId?: string;
  tpDescription?: string;
  notes: string;
  materialLink?: string;
  documentationUrl?: string; // New field for documentation image
  status: 'Selesai' | 'Belum Selesai' | 'Jam kosong';
  teacherId?: string;
  teacherName?: string;
  tpCode?: string;
  isAttendanceOptional?: boolean;
  academicYear?: string;
  semester?: string;
  isArchived?: boolean;
}

export interface ScheduleEntry {
  id: string;
  day: 'Senin' | 'Selasa' | 'Rabu' | 'Kamis' | 'Jumat';
  period: number;
  time: string;
  subject: string;
  class: string;
  isBreak?: boolean;
}

export interface AssessmentGrade {
  id: string; // Combined ID: class-tpId-activityType
  class: string;
  tpId: string;
  subject?: string;
  activityType: ActivityType; // Use enum
  assessmentType?: 'Sumatif per TP' | 'KBM dan quiz (jika ada)' | 'Sumatif Akhir' | 'KBM dan Asesmen Harian' | 'Praktikum TP'; // New field
  date: string;
  scores: Record<string, number>; // studentId -> score
  teacherId?: string;
  academicYear?: string;
  semester?: string;
}

export interface SasGrade {
  id: string;
  studentId: string;
  score: number;
  nilaiEvaluasi?: number;
  nilaiRapor?: number;
  nilaiTh?: number;
  nilaiCatatan?: number;
  class: string;
  subject: string;
  teacherId?: string;
  date: string;
  SasVisibility?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

export type FormativeLevel = 'Awal Berkembang' | 'Layak' | 'Mahir';

export interface FormativeGradeTP {
  id: string; // class-tpId
  class: string;
  tpId: string;
  levels: Record<string, FormativeLevel>; // studentId -> level
  summativeScores?: Record<string, number>; // studentId -> score
  praktikumScores?: Record<string, number>; // studentId -> score
  teacherId?: string;
}

export interface GradeEntry {
  studentId: string;
  studentName: string;
  formative: number;
  summative: number;
  task: number;
}

export interface Announcement {
  id: string;
  text: string;
  type: 'info' | 'urgent' | 'success';
}

export interface PeriodTime {
  period: number;
  startTime: string;
  endTime: string;
}

export interface SchoolSettings {
  id?: string;
  schoolName: string;
  governmentName?: string;
  departmentName?: string;
  principalName: string;
  principalNip: string;
  address: string;
  additionalKopLine?: string;
  logoUrl?: string;
  departmentLogoUrl?: string;
  academicCalendarUrl?: string;
  academicYear?: string;
  semester?: string;
  kecamatan?: string;
  kabupaten?: string;
  announcements?: Announcement[];
  classes?: string[];
  showGradesToStudents?: boolean;
  isStudentMaintenanceMode?: boolean;
  periodTimes?: PeriodTime[];
  dayPeriodTimes?: Record<string, PeriodTime[]>;
  semesterStartDate?: string;
  semesterEndDate?: string;
  customHolidays?: { date: string; description: string }[];
  classCustomHolidays?: Record<string, { date: string; description: string }[]>;
  classSemesterDates?: Record<string, { startDate: string; endDate: string }>;
}

export interface CourseSubmission {
  id: string;
  moduleId: string;
  chapterId: string;
  studentId: string;
  score: number;
  mistakes: number;
  submittedAt: number;
}

export interface CourseQuiz {
  question: string;
  options: string[];
  correctAnswerIndex: number;
}

export interface CourseChapter {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
  quiz: CourseQuiz[];
}

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  subject: string;
  teacherId: string;
  chapters: CourseChapter[];
  targetClasses: string[];
  quizCooldownMinutes?: number;
  createdAt: number;
  updatedAt: number;
}

export interface TugasQuestion {
  id: string;
  text: string;
  type?: 'multiple-choice' | 'essay';
  options: string[]; // Always 4 options for multiple-choice
  correctAnswerIndex: number; // 0 to 3
  points?: number;
  timeLimit?: number;
}

export enum TugasType {
  TUGAS = 'Tugas',
  TUGAS_NILAI_TAMBAH = 'Tugas Nilai Tambah'
}

export interface Tugas {
  id: string;
  title: string;
  description?: string;
  type: TugasType;
  teacherId: string;
  subject: string;
  class: string; // Legacy, keep for backward compatibility
  targetClasses?: string[];
  targetStudents?: string[];
  questions: TugasQuestion[];
  createdAt: string;
  dueDate?: string;
  isActive: boolean;
  startTime?: string;
  endTime?: string;
  isSummative?: boolean;
  tpId?: string;
  forceSequential?: boolean;
  isExtraCredit?: boolean;
  extraCreditTargetTpId?: string;
  fullscreenRequired?: boolean;
}

export interface TugasSubmission {
  id: string;
  tugasId: string;
  studentId: string;
  score: number;
  answers: Record<string, number | string>; // questionId -> selectedOptionIndex or essay text
  essayScores?: Record<string, number>; // questionId -> score
  submittedAt: string;
  isAddedToTp?: boolean;
  isPgAddedToTp?: boolean;
  isEssayAddedToTp?: boolean;
}

export interface TugasSession {
  id: string;
  tugasId: string;
  studentId: string;
  studentName: string;
  studentClass: string;
  tugasTitle: string;
  startTime: string;
  lastActive: string;
  status: 'working' | 'finished' | 'idle';
}

export type HomeroomJournalCategory =
  | 'Akademik'
  | 'Kehadiran'
  | 'Kedisiplinan'
  | 'Karakter'
  | 'Sosial'
  | 'Kebersihan'
  | 'Tanggung jawab'
  | 'Prestasi'
  | 'Pelanggaran'
  | 'Lainnya';

export type HomeroomJournalStatus =
  | 'Belum Diproses'
  | 'Dalam Proses'
  | 'Selesai'
  | 'Perlu Panggilan Ortu'
  | 'Perlu Konseling BK';

export interface HomeroomJournalNote {
  id: string;
  date: string;
  homeroomClass: string;
  studentId?: string;
  studentName: string;
  category: HomeroomJournalCategory;
  description: string;
  actionTaken: string;
  result?: string;
  status: HomeroomJournalStatus;
  nextNotes?: string;
  teacherId?: string;
  teacherName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function normalizeClassName(cls?: string): string {
  if (!cls) return '';
  let str = cls.toString().trim().toUpperCase();
  str = str.replace(/^IX[-.\s]?/i, '9');
  str = str.replace(/^VIII[-.\s]?/i, '8');
  str = str.replace(/^VII[-.\s]?/i, '7');
  return str.replace(/[^A-Z0-9]/g, '');
}

export function isSameClass(class1?: string, class2?: string): boolean {
  if (!class1 || !class2) return false;
  if (class1.trim() === class2.trim()) return true;
  return normalizeClassName(class1) === normalizeClassName(class2);
}

export interface SubjectGrade {
  subjectName: string;
  grade: number;
}

export interface SemesterProgress {
  semesterName: string;
  subjects: SubjectGrade[];
}

export interface StudentAcademicProgress {
  id: string;
  studentId: string;
  class: string;
  semesters: SemesterProgress[];
  createdAt: number;
  updatedAt: number;
}

export type ExtracurricularGrade = 'A' | 'B' | 'C' | 'D' | 'Sangat Baik' | 'Baik' | 'Cukup' | 'Kurang';

export interface StudentExtracurricular {
  id: string;
  studentId: string;
  studentName: string;
  class: string;
  extraName: string;
  grade: ExtracurricularGrade;
  description?: string;
  semester?: string; // '1' | '2' | 'Ganjil' | 'Genap'
  academicYear?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ScoutRole = 'Pratama' | 'Pinru' | 'Pratama & Pinru' | 'Wapinru' | 'Anggota';

export interface ScoutMember {
  id: string;
  studentId?: string;
  name: string;
  class: string;
  nta?: string; // Nomor Tanda Anggota Pramuka
  role: ScoutRole;
  gender?: string;
  notes?: string;
}

export interface ScoutRegu {
  id: string;
  name: string; // e.g. "Regu Garuda", "Regu Melati"
  category: 'Putra' | 'Putri';
  motto?: string;
  members: ScoutMember[];
}

export interface ScoutAdministrationData {
  id?: string;
  homeroomClass: string;
  gugusDepan: string; // e.g. "01.001 - 01.002 Pangkalan SMPN 1 Kawali"
  namaPasukan: string; // e.g. "Pasukan Penggalang Ki Hajar Dewantara"
  pembinaPutra?: string;
  pembinaPutri?: string;
  reguList: ScoutRegu[];
  academicYear?: string;
  semester?: string;
  updatedAt?: string;
}

export type GuidanceCategory =
  | 'Pribadi'
  | 'Sosial'
  | 'Belajar / Akademik'
  | 'Kedisiplinan & Karakter'
  | 'Karir & Studi Lanjut'
  | 'Keluarga & Ekonomi'
  | 'Lainnya';

export type GuidanceStatus =
  | 'Selesai'
  | 'Dalam Pemantauan'
  | 'Perlu Bimbingan Lanjutan'
  | 'Perlu Panggilan Orang Tua'
  | 'Dirujuk ke Guru BK';

export interface RombelGuidanceNote {
  id: string;
  teacherId: string;
  teacherName: string;
  studentId?: string;
  studentName: string;
  studentNis?: string;
  studentClass?: string;
  date: string;
  category: GuidanceCategory;
  topic: string;
  description: string;
  guidanceGiven: string;
  followUp: string;
  status: GuidanceStatus;
  result?: string;
  location?: string;
  counselingType?: 'Individu' | 'Kelompok';
  createdAt?: string;
  updatedAt?: string;
}

